import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { get, post } from 'helpers/api_helper'
import { store as socketStore } from 'components/Common/Socket/store'
import { newPlayerOperationId, readPending, savePending } from './PlayerAccounts/playerInvitationState'

export default function AccountInvitationPanel(props) {
  return <InvitationContent key={props.resource + ':' + props.actorId + ':' + props.id + ':' + Boolean(props.readOnly)} {...props} />
}

function InvitationContent({ id, actorId, resource, accountName, labels, runCommand, readOnly = false }) {
  const { socket, socketClientId } = useContext(socketStore)?.state?.socket || {}
  const key = 'sportya-' + resource + '-invitation:' + actorId + ':' + id
  const pending = useRef(readOnly ? null : readPending(key))
  const request = useRef(null)
  const busy = useRef(false)
  const [info, setInfo] = useState(null)
  const [error, setError] = useState('')
  const [status, setStatus] = useState(pending.current ? 'unknown' : 'idle')
  const [retry, setRetry] = useState(0)
  const [clock, setClock] = useState(Date.now())
  useEffect(() => {
    let active = true, timer
    const started = Date.now()
    const read = async () => {
      try {
        const response = await get('/admin/' + resource + '-invitations/' + id)
        if (!response?.data || typeof response.data.eligible !== 'boolean') throw new Error('Invalid response')
        if (!active) return
        const data = response.data
        const waiting = ['pending', 'sending'].includes(data.invitationStatus)
        setInfo(waiting && Date.now() - started >= 120000 ? { ...data, invitationStatus: 'unconfirmed' } : data)
        setError('')
        if (waiting && Date.now() - started < 120000) timer = setTimeout(read, 2000)
      } catch { if (active) setError('Invitation status could not be loaded. Retry to check the account.') }
    }
    read()
    const tick = setInterval(() => setClock(Date.now()), 1000)
    return () => { active = false; clearTimeout(timer); clearInterval(tick) }
  }, [id, resource, retry])
  const track = useCallback(async (attempt, resubmit = true) => {
    if (busy.current || readOnly) return
    if (resubmit) attempt = { ...attempt, correlationId: newPlayerOperationId() }
    busy.current = true; pending.current = attempt; savePending(key, attempt)
    setStatus('pending'); setError('')
    const operation = runCommand({ ...attempt, operation: resource + '-invitation-requested', socket,
      read: operationId => get('/admin/' + resource + '-invitations/' + id + '/operations/' + operationId),
      submit: resubmit ? () => post('/admin/' + resource + '-invitations/' + id, { operationId: attempt.operationId, socketClientId },
        { headers: { 'X-Correlation-Id': attempt.correlationId } }) : undefined,
    })
    request.current = operation
    const result = await operation.promise
    if (request.current !== operation) return
    request.current = null; busy.current = false
    if (result.status === 'cancelled') return
    if (result.status === 'saved' || result.status === 'error') {
      pending.current = null; savePending(key, null)
      if (result.status === 'error') setError(result.message)
      else setInfo(current => ({ ...current, invitationStatus: result.invitationStatus, retryAt: new Date(Date.now() + 300000).toISOString() }))
      setRetry(value => value + 1)
    }
    setStatus(result.status)
  }, [id, key, resource, runCommand, socket, socketClientId, readOnly])
  useEffect(() => {
    const timer = setTimeout(() => { if (pending.current) track(pending.current, false) }, 0)
    return () => { clearTimeout(timer); const operation = request.current; request.current = null; busy.current = false; operation?.cancel() }
  }, [track])
  if (info?.eligible === false && !info?.invitationStatus && !pending.current) return null
  const locked = status === 'pending' || status === 'unknown'
  const waitingSeconds = Math.max(0, Math.ceil((new Date(info?.retryAt).getTime() - clock) / 1000))
  return <section className="card" aria-label={accountName + ' invitation'}><div className="card-body">
    <h5>Account invitation</h5>
    {!info && !error && <p role="status">Loading invitation status…</p>}
    {info && <p role="status">{labels[info.invitationStatus] || 'Invitation status is not confirmed.'}</p>}
    {info?.eligible && info.invitationStatus !== 'consumed' && info?.expiresAt && new Date(info.expiresAt).getTime() < clock && <p>The invitation link has expired. Send a new invitation to let the {accountName.toLowerCase()} activate the account.</p>}
    {error && <p role="alert" className="text-danger">{error}</p>}
    {status === 'unknown' && <p role="status">The request result is not confirmed. Check its status before requesting another invitation.</p>}
    <div className="d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
      {!readOnly && <button type="button" className="btn btn-primary d-inline-flex align-items-center justify-content-center" disabled={!info?.eligible || Boolean(error) || locked || waitingSeconds > 0}
        onClick={() => track({ operationId: newPlayerOperationId(), correlationId: newPlayerOperationId() })}>
        {status === 'pending' ? 'Requesting invitation…' : 'Resend invitation'}</button>}
      <button type="button" className="btn btn-light" disabled={status === 'pending'} onClick={() => {
        if (pending.current) track(pending.current); else setRetry(value => value + 1)
      }}>{pending.current ? 'Resume invitation request' : 'Check invitation status'}</button>
      {!readOnly && waitingSeconds > 0 && <small className="text-muted">Resend available in {Math.floor(waitingSeconds / 60)}:{String(waitingSeconds % 60).padStart(2, '0')}.</small>}
    </div>
    <small className="d-block text-muted mt-2">A new invitation replaces the previous link. It is valid for 24 hours and can be used once.</small>
  </div></section>
}
